import React, { useState, useEffect, useMemo } from 'react';
import { find } from 'lodash';
import PropTypes from 'prop-types';
import { Controller, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import {
  Autocomplete,
  Button,
  Box,
  Card,
  CardActions,
  CardContent,
  Collapse,
  EyeIcon,
  EyeSlashIcon,
  FormLabel,
  Grid,
  HelpCircleIcon,
  IconButton,
  InputAdornment,
  Paper,
  TextField,
  Typography,
} from '@availity/element';
import envVar, { getSpecificEnv } from '@availity/env-var';

import { Footer, MessageDisplay, NonProdBanner, SecurePassword } from '@/components';
import { useAppContext } from '@/context';
import { errorMessages, regions } from '@/global';
import { useFRCallback } from '@/hooks';
import { avLogoWhite, avBackgroundOrange } from '@/static';

const currentEnv = getSpecificEnv();
const shouldShowNonProdBanner = currentEnv !== 'prod';

const handleRegionsHelp = () => {
  const regionsHelpLink = envVar({
    prod: 'https://apps.availity.com/availity/help-fields/index.html?av-contextId=3c57e378-c3c3-4f37-b7a4-cb2b1b1908da',
    qa: 'https://qa-apps.availity.com/availity/help-fields/index.html?av-contextId=3c57e378-c3c3-4f37-b7a4-cb2b1b1908da',
    local:
      'https://test-apps.availity.com/availity/help-fields/index.html?av-contextId=3c57e378-c3c3-4f37-b7a4-cb2b1b1908da',
  });
  window.open(regionsHelpLink);
};

const getRegionObjectByValue = (value) => {
  const matchedRegionObject = find(regions, (region) => region.label === value);

  return matchedRegionObject.value;
};

const NewUserEnrollmentInvitation = ({ ...props }) => {
  const { stepToRender, setCurrentStep, errorCode, userInvitationDetails, formValues, setFormValues } = props;
  const {
    addMessage,
    setMessages,
    addInvalidNewEmail,
    takenUserIds,
    addTakenUserId,
    invalidUserIds,
    addInvalidUserId,
    addTakenEmail,
    passVars,
    setPassVars,
  } = useAppContext();
  const { getTextInputCallbackByName } = useFRCallback(stepToRender);
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();

  const { firstName, lastName, emailAd: email } = userInvitationDetails;

  const { newUserEnrollment: newUserEnrollmentValues } = formValues;

  const [currentUserIdValue, setCurrentUserIdValue] = useState(newUserEnrollmentValues.userId || '');
  const [currentPasswordValue, setCurrentPasswordValue] = useState(newUserEnrollmentValues.password || '');
  const [showPasswordRequirements, setShowPasswordRequirements] = useState(false);

  // initialize form
  const {
    register,
    handleSubmit,
    formState: { errors },
    getValues,
    setError,
    control,
  } = useForm({
    defaultValues: {
      userId: newUserEnrollmentValues.userId || '',
      password: newUserEnrollmentValues.password || '',
      region: newUserEnrollmentValues.region || '',
    },
  });

  // handle setting passVars when userId or password changes
  useEffect(() => {
    const lowercasePassword = currentPasswordValue?.toLowerCase();
    const lowercaseUserId = currentUserIdValue?.toLowerCase();

    const passHas8To15 = /^.{8,15}$/.test(currentPasswordValue); // length
    const passHasNumber = /\d/.test(currentPasswordValue); // contains digit
    const passHasUpperCase = /[A-Z]/.test(currentPasswordValue); // contains Uppercase
    const passHasLowerCase = /[a-z]/.test(currentPasswordValue); // contains Lowercase
    const passHasSpecial = /[!"#$%&'()*+,./:;<=>?@[\\\]^_`{|}~-]/.test(currentPasswordValue); // contains special char;
    const passDoesntContainSpaces = currentPasswordValue?.length > 0 && /^\S*$/.test(currentPasswordValue); // no spaces in pass

    // NOTE: the business logic has been updated such that the password cannot contain a 5 character sequence within the userId
    // substring match logic
    // iterate over length of userid and check 5 chars at a time until end of userid is reached (length -4)
    let passContainsUserIdString = false;

    for (let uidStartIndex = 0; uidStartIndex < lowercaseUserId.length - 4; uidStartIndex++) {
      const uidEndIndex = uidStartIndex + 5;
      const substringToTest = lowercaseUserId.slice(uidStartIndex, uidEndIndex);

      if (lowercasePassword?.indexOf(substringToTest?.toLowerCase()) >= 0) {
        passContainsUserIdString = true;
      }
    }

    setPassVars({
      ...passVars,
      passHas8To15,
      passHasNumber,
      passHasUpperCase,
      passHasLowerCase,
      passHasSpecial,
      passDoesntContainUserIdString: lowercasePassword?.length > 0 && !passContainsUserIdString,
      passDoesntContainSpaces,
    });
  }, [currentUserIdValue, currentPasswordValue]);

  const currentFormValues = getValues();

  // handle error codes from FR and populate the errors in the form - or show a toaster message as needed
  useEffect(() => {
    if (['150', '153', '154'].indexOf(errorCode) >= 0) {
      addMessage('Oops! Something went wrong. Please try again.');
    }

    if (['109', '118'].indexOf(errorCode) >= 0) {
      const { email } = currentFormValues;
      addInvalidNewEmail(email);
      setError(
        'email',
        { type: 'isInvalid', message: 'The email address you entered is not valid. Please try again.' },
        true
      );
    }

    if (['114', '151', '157', '159'].indexOf(errorCode) >= 0) {
      const { email } = currentFormValues;
      addTakenEmail(email);
      setError(
        'email',
        { type: 'isEmailTaken', message: 'Looks like you have an Availity account. Click "Log In" to continue.' },
        true
      );
    }

    if (errorCode === '126') {
      const { userId } = currentFormValues;
      addTakenUserId(userId);
      setError(
        'userId',
        {
          type: 'isUserIdTaken',
          message:
            'This User ID is already taken. Already have an Availity account with this User ID? Click "Log In" to continue.',
        },
        true
      );
    }

    if (['152', '158'].indexOf(errorCode) >= 0) {
      const { userId } = currentFormValues;
      addInvalidUserId(userId);
      setError(
        'userId',
        {
          type: 'isUserIdInvalid',
          message: errorMessages.userId.format,
        },
        true
      );
    }

    if (['160'].indexOf(errorCode) >= 0) {
      setError('password', {
        type: 'isCommonPassword',
        message: 'Common passwords such as "Password123" will not be accepted.',
      });
    }

    if (['161'].indexOf(errorCode) >= 0) {
      setError('password', {
        type: 'passwordContainsUserIdSequence',
        message: errorMessages.password.valid,
      });
    }
  }, [errorCode]);

  const regionOptions = useMemo(() => {
    const output = regions.map((region) => region.label);

    return output;
  }, []);

  const onSubmit = (values) => {
    setMessages([]);
    setFormValues({ ...formValues, newUserEnrollment: { ...values } });
    const output = stepToRender;

    if (values) {
      const { payload = {} } = output;
      const { callbacks = [] } = payload;

      const newCallbacks = [...callbacks];

      const { userId, password, region } = values;

      // handle each callback one at a time
      const firstNameCallbackInfo = getTextInputCallbackByName('firstName');
      newCallbacks[firstNameCallbackInfo?.callbackIndex].input[0].value = firstName;

      const lastNameCallbackInfo = getTextInputCallbackByName('lastName');
      newCallbacks[lastNameCallbackInfo?.callbackIndex].input[0].value = lastName;

      const emailCallbackInfo = getTextInputCallbackByName('email');
      newCallbacks[emailCallbackInfo?.callbackIndex].input[0].value = email;

      const userIdCallbackInfo = getTextInputCallbackByName('userId');
      newCallbacks[userIdCallbackInfo?.callbackIndex].input[0].value = userId;

      const passwordCallbackInfo = getTextInputCallbackByName('password');
      newCallbacks[passwordCallbackInfo?.callbackIndex].input[0].value = password;

      const regionCallbackInfo = getTextInputCallbackByName('regioncode');
      const regionObject = getRegionObjectByValue(region);
      newCallbacks[regionCallbackInfo?.callbackIndex].input[0].value = regionObject?.code;

      // set the new callbacks in the output
      Object.assign(output.payload.callbacks, [...newCallbacks]);
    }

    setCurrentStep({ ...output });
  };

  return (
    <Grid
      id="new-user-enrollment-page-grid-container"
      container
      display="flex"
      flexDirection="column"
      flexWrap="wrap"
      spacing={0}
      sx={{ minHeight: '100vh', justifyContent: 'center' }}
    >
      {shouldShowNonProdBanner ? (
        <Grid
          id="nonprod-banner-grid-container"
          display="flex"
          mx={2}
          mt={2}
          justifyContent="center"
          sx={{ marginBottom: { xs: 2, md: showPasswordRequirements ? 2 : 0 } }}
        >
          <NonProdBanner />
        </Grid>
      ) : (
        <Box />
      )}
      <Grid
        id="new-user-enrollment-page-content-grid"
        container
        display="flex"
        flexDirection="row"
        justifyContent="center"
        flexWrap="wrap"
        mt="auto"
      >
        <Paper
          variant="elevation"
          elevation={6}
          sx={{
            width: { xs: '350px', sm: '500px', md: '600px', lg: '800px' },
            marginRight: { sm: 2 },
            marginBottom: { xs: 2, md: 0 },
          }}
        >
          <form onSubmit={handleSubmit(onSubmit)}>
            <CardContent>
              {/* <Typography variant="h1" mb={2}>
                Create User Account
              </Typography> */}
              <Typography variant="h2" mb={2}>
                You&apos;ve been invited to join Availity.
              </Typography>
              <Typography variant="subtitle1" mb={2}>
                Verify the details below from your invite and complete the form to set up your new account.
              </Typography>
              <Paper sx={{ marginBottom: 3 }}>
                <CardContent>
                  <Typography variant="subtitle1">
                    <Box component="span" sx={{ fontWeight: 'bold' }}>
                      First Name
                    </Box>{' '}
                    {firstName}
                  </Typography>
                  <Typography variant="subtitle1">
                    <Box component="span" sx={{ fontWeight: 'bold' }}>
                      Last Name
                    </Box>{' '}
                    {lastName}
                  </Typography>
                  <Typography variant="subtitle1">
                    <Box component="span" sx={{ fontWeight: 'bold' }}>
                      Email Address
                    </Box>{' '}
                    {email}
                  </Typography>
                </CardContent>
              </Paper>
              <Grid container display="flex" flexDirection="row" spacing={2}>
                <Grid size={{ xs: 12, sm: 12, md: 6 }}>
                  <TextField
                    id="userId"
                    label="User ID"
                    margin="normal"
                    {...register('userId', {
                      onChange: (e) => {
                        const { target = {} } = e;
                        const { value } = target;

                        if (value) {
                          setCurrentUserIdValue(value);
                        }
                      },
                      minLength: {
                        value: 6,
                        message: errorMessages.userId.format,
                      },
                      maxLength: {
                        value: 15,
                        message: errorMessages.userId.format,
                      },
                      pattern: {
                        value: /^[\dA-Za-z]*$/,
                        message: errorMessages.userId.format,
                      },
                      required: errorMessages.userId.format,
                      validate: {
                        isTaken: (value) => {
                          let errorMsg = false;
                          if (takenUserIds.indexOf(value) >= 0) {
                            errorMsg = errorMessages.userId.taken;
                          }

                          return errorMsg || true;
                        },
                        isInvalid: (value) => {
                          let errorMsg = false;
                          if (invalidUserIds.indexOf(value) >= 0) {
                            errorMsg = errorMessages.userId.format;
                          }

                          return errorMsg || true;
                        },
                      },
                    })}
                    error={!!errors.userId}
                    helperText={errors.userId?.message}
                  />
                </Grid>
              </Grid>
              <Grid container display="flex" flexDirection="row" spacing={2} mb={1}>
                <Grid size={{ xs: 12, sm: 12, md: 6 }}>
                  <TextField
                    id="password"
                    label="Password"
                    type={showPassword ? 'text' : 'password'}
                    margin="normal"
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            title="password visibility"
                            onClick={() => setShowPassword((prev) => !prev)}
                            edge="end"
                          >
                            {showPassword ? <EyeIcon fontSize="small" /> : <EyeSlashIcon fontSize="small" />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    {...register('password', {
                      onChange: (e) => {
                        const { target = {} } = e;
                        const { value } = target;

                        if (!showPasswordRequirements) {
                          setShowPasswordRequirements(true);
                        }

                        setCurrentPasswordValue(value);
                      },
                      minLength: {
                        value: 8,
                        message: errorMessages.password.valid,
                      },
                      maxLength: {
                        value: 15,
                        message: errorMessages.password.valid,
                      },
                      pattern: {
                        value:
                          /^(?=.*[A-Z])(?=.*[a-z])(?=.*\d)(?!.*\s)(?=.*[!"#$%&'()*+,./:;<=>?@[\\\]^_`{|}~-])[\w!"#$%&'()*+,./:;<=>?@[\\\]^`{|}~-]{8,15}$/,
                        message: errorMessages.password.valid,
                      },
                      required: 'Enter a valid password.',
                      validate: {
                        pwdDoesntContainUserIdString: (value) => {
                          // NOTE: the business logic has been updated such that the password cannot contain a 5 character sequence within the userId

                          const formValues = getValues();
                          const { userId } = formValues;

                          // need to encode inputs to play nicely with regex
                          const escapedUserId = userId?.toLowerCase()?.replace(/(?=[ ()[\\\]])/g, '\\');
                          const escapedValue = value?.toLowerCase()?.replace(/(?=[ ()[\\\]])/g, '\\');

                          // substring match logic
                          // iterate over length of userid and check 5 chars at a time until end of userid is reached (length -4)
                          let passDoesntContainUserIdString = true;

                          for (let uidStartIndex = 0; uidStartIndex < escapedUserId.length - 4; uidStartIndex++) {
                            const uidEndIndex = uidStartIndex + 5;
                            const substringToTest = escapedUserId.slice(uidStartIndex, uidEndIndex);

                            if (escapedValue?.toLowerCase().indexOf(substringToTest?.toLowerCase()) >= 0) {
                              passDoesntContainUserIdString = false;
                            }
                          }

                          // fail test with false when the userId is blank, since we cant really check if the pass contains it if its not there yet
                          return userId && !passDoesntContainUserIdString ? errorMessages.password.valid : true;
                        },
                      },
                    })}
                    error={!!errors.password}
                    helperText={errors.password?.message}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 12, md: 6 }}>
                  <FormLabel id="regions-label" htmlFor="regions">
                    <Box display="flex" flexWrap="wrap">
                      <IconButton
                        color="primary"
                        title="Regions Help"
                        role="link"
                        onClick={() => handleRegionsHelp()}
                        aria-describedby="regions-help"
                        size="small"
                        sx={{ fontSize: 'inherit', marginTop: '-2px', opacity: 1 }}
                      >
                        <HelpCircleIcon aria-hidden />{' '}
                      </IconButton>
                      Choose a region
                    </Box>
                  </FormLabel>
                  <Controller
                    name="region"
                    control={control}
                    render={({ field: { onChange, onBlur, value } }) => (
                      <Autocomplete
                        id="autocomplete-region"
                        onChange={(_, value, reason) => {
                          if (reason === 'clear') {
                            onChange(null);
                          }
                          onChange(value);
                        }}
                        value={value || null}
                        onBlur={onBlur}
                        FieldProps={{
                          id: 'region',
                          error: !!errors.region,
                          helperText: errors.region?.message || "Select one, don't worry, you can add more later",
                          margin: 'normal',
                        }}
                        options={regionOptions}
                        disableClearable
                      />
                    )}
                    {...register('region', {
                      required: errorMessages.region,
                    })}
                  />
                </Grid>
              </Grid>
              <Collapse in={showPasswordRequirements}>
                <SecurePassword />
              </Collapse>
              <Grid mt={2}>
                <MessageDisplay />
              </Grid>
            </CardContent>
            <CardActions mt={3} sx={{ justifyContent: 'end' }}>
              <Button color="primary" type="submit">
                Continue
              </Button>
            </CardActions>
          </form>
        </Paper>
        <Card
          variant="elevation"
          elevation={6}
          sx={{
            alignItems: 'stretch',
            backgroundImage: `url(${avBackgroundOrange})`,
            backgroundSize: 'cover',
            backgroundClip: 'border-box',
          }}
        >
          <Grid display="flex" flexDirection="column" p={3} justifyContent="space-between" sx={{ height: '100%' }}>
            <Grid display="flex" mb="auto" justifyContent="end">
              <img
                src={avLogoWhite}
                alt="Availity Logo"
                style={{
                  maxWidth: '60%',
                }}
              />
            </Grid>
            <Grid display="flex" mb={2} justifyContent="center">
              <Typography variant="subtitle1">
                <Box component="span" sx={{ fontWeight: 'bold', color: '#ffffff' }}>
                  Already have an account?
                </Box>
              </Typography>
            </Grid>
            <Grid mb="auto">
              <Button
                color="secondary"
                onClick={() => {
                  setMessages([]); // clear out messages on route navigation
                  navigate('/login');
                }}
                sx={{ width: '100%' }}
              >
                Log in.
              </Button>
            </Grid>
          </Grid>
        </Card>
      </Grid>
      <Footer />
    </Grid>
  );
};

NewUserEnrollmentInvitation.propTypes = {
  stepToRender: PropTypes.object.isRequired,
  setCurrentStep: PropTypes.func.isRequired,
  errorCode: PropTypes.string,
  userInvitationDetails: PropTypes.object.isRequired,
  formValues: PropTypes.object.isRequired,
  setFormValues: PropTypes.func.isRequired,
};

export default NewUserEnrollmentInvitation;
